import type React from 'react';
import { useEffect } from 'react';

import { type ChangeEvent, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { RotateCcw, Menu } from 'lucide-react';
import { useIsDesktopSize } from '@/hooks/useIsDesktopSize';
import type { ViewState } from 'react-map-gl/mapbox';
import usePrevious from '@/hooks/usePrevious.ts';

interface MapTopBarProps {
	viewState: ViewState;
	onReset: () => void;
	onViewStateChange: (viewState: ViewState) => void;
	children?: React.ReactNode;
}

const LAT_MIN_MAX = { min: -90, max: 90 };
const LONG_MIN_MAX = { min: -180, max: 180 };
const ZOOM_MIN_MAX = { min: 0, max: 20 };

const isNumericSymbol = (value: string): boolean => value === '' || value === '+' || value === '-';

export function MapTopBar({ viewState, onReset, onViewStateChange, children }: MapTopBarProps) {
	const { isDesktop, isTablet, isMobile } = useIsDesktopSize();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isInputFocused, setIsInputFocused] = useState(false);

	const [latitude, setLatitude] = useState(viewState.latitude.toFixed(2));
	const [longitude, setLongitude] = useState(viewState.longitude.toFixed(2));
	const [zoom, setZoom] = useState(viewState.zoom.toFixed(2));
	const prevViewState = usePrevious(viewState);

	const showInlineControls = isDesktop;
	const showSheetControls = isTablet || isMobile;

	useEffect(() => {
		if (isInputFocused) {
			return;
		}

		if (
			prevViewState &&
			(prevViewState.latitude !== viewState.latitude ||
				prevViewState.longitude !== viewState.longitude ||
				prevViewState.zoom !== viewState.zoom)
		) {
			setLatitude(viewState.latitude.toFixed(2));
			setLongitude(viewState.longitude.toFixed(2));
			setZoom(viewState.zoom.toFixed(2));
		}
	}, [viewState, prevViewState, isInputFocused]);

	const onLatitudeChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
		if (isNumericSymbol(value)) {
			setLatitude(value);
			return;
		}

		const parsed = Number.parseFloat(value);

		if (!isNaN(parsed)) {
			const clamped = Math.max(LAT_MIN_MAX.min, Math.min(LAT_MIN_MAX.max, parsed));
			onViewStateChange({ ...viewState, latitude: clamped });
			setLatitude(clamped.toString());
		} else {
			setLatitude('0');
		}
	};

	const onLongitudeChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
		if (isNumericSymbol(value)) {
			setLongitude(value);
			return;
		}

		const parsed = Number.parseFloat(value);

		if (!isNaN(parsed)) {
			const clamped = Math.max(LONG_MIN_MAX.min, Math.min(LONG_MIN_MAX.max, parsed));
			onViewStateChange({ ...viewState, longitude: clamped });
			setLongitude(clamped.toString());
		} else {
			setLongitude('0');
		}
	};

	const onZoomChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
		if (isNumericSymbol(value)) {
			setZoom(value);
			return;
		}

		const parsed = Number.parseFloat(value);

		if (!isNaN(parsed)) {
			const clamped = Math.max(ZOOM_MIN_MAX.min, Math.min(ZOOM_MIN_MAX.max, parsed));
			onViewStateChange({ ...viewState, zoom: clamped });
			setZoom(clamped.toString());
		} else {
			setZoom('0');
		}
	};

	return (
		<Card className="relative mx-4 my-4 z-10 px-4 py-2 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border shadow-lg">
			<div className="flex items-center justify-between text-sm">
				<div className="flex items-center gap-4">
					<div className="flex items-center gap-3">
						<div className="flex items-center gap-1">
							<span className="text-slate-600 dark:text-slate-400 text-xs">Lng:</span>
							<Input
								value={longitude}
								type="number"
								min={-180}
								max={180}
								onChange={onLongitudeChange}
								onFocus={() => setIsInputFocused(true)}
								onBlur={() => setIsInputFocused(false)}
								className="h-6 w-20 text-xs font-mono px-1"
							/>
						</div>
						<div className="flex items-center gap-1">
							<span className="text-slate-600 dark:text-slate-400 text-xs">Lat:</span>
							<Input
								value={latitude}
								type="number"
								min={-90}
								max={90}
								onChange={onLatitudeChange}
								onFocus={() => setIsInputFocused(true)}
								onBlur={() => setIsInputFocused(false)}
								className="h-6 w-20 text-xs font-mono px-1"
							/>
						</div>
						<div className="flex items-center gap-1">
							<span className="text-slate-600 dark:text-slate-400 text-xs">Zoom:</span>
							<Input
								type="number"
								min={0}
								max={ZOOM_MIN_MAX.max}
								value={zoom}
								onChange={onZoomChange}
								onFocus={() => setIsInputFocused(true)}
								onBlur={() => setIsInputFocused(false)}
								className="h-6 w-16 text-xs font-mono px-1"
							/>
						</div>
					</div>

					{showInlineControls && <div className="flex items-center gap-2">{children}</div>}
				</div>

				<div className="flex items-center gap-2">
					{showSheetControls && (
						<Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
							<SheetTrigger asChild>
								<Button
									variant="outline"
									size="sm"
									className="h-8 w-8 p-0 bg-white/50 dark:bg-slate-800/50"
								>
									<Menu className="h-3 w-3" />
								</Button>
							</SheetTrigger>
							<SheetContent side="right" className="w-80 p-6">
								<SheetHeader className="mb-6">
									<SheetTitle>Map Controls</SheetTitle>
								</SheetHeader>
								<div className="space-y-4">{children}</div>
							</SheetContent>
						</Sheet>
					)}

					<Button
						variant="outline"
						size="sm"
						onClick={onReset}
						className="h-8 px-3 bg-white/50 dark:bg-slate-800/50 hover:bg-white/80 dark:hover:bg-slate-800/80"
					>
						<RotateCcw className="h-3 w-3 mr-1" />
						Reset
					</Button>
				</div>
			</div>
		</Card>
	);
}
